// web/src/i18n/index.ts
//
// i18next bootstrap. Loaded once from main.tsx before <App /> renders so
// the catalogs are warm by first paint. Resolution order in
// LanguageProvider takes precedence over the detector — this file just
// configures the backend, fallback, namespace policy, and detector.
//
// Catalogs: web/public/locales/{lang}/{ns}.json
// Eager namespaces: common, errors, enums (small + universally used)
// Lazy namespaces:  dashboard, notes, copilot, monitors, calendar, auth, preferences
import i18n, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

// `true` when running under Vitest / jsdom (no real fetch backend, no
// /locales/*.json available). Loading the HTTP backend in that environment
// causes initI18n() to hang on the first request. The test helper
// `renderWithLocale` works against synchronous resources bundled at build
// time (see `loadTestResources` below) so tests render real strings, not
// missing-key fallbacks.
// Vitest sets `import.meta.env.MODE === "test"` automatically.
const IS_TEST_ENV = import.meta.env.MODE === "test";

/**
 * In the test environment we synchronously require the EN catalogs so
 * tests render real strings (matching production), but skip HTTP backend
 * + LanguageDetector network paths (which hang under jsdom). TR + AR
 * catalogs are intentionally empty in Slice 1 — tests fall back to EN.
 *
 * Wrapped in a function so the import is lazy (it only runs in test
 * mode) and so the production bundle does not include the catalog JSON.
 */
async function loadTestResources(): Promise<Resource> {
  const namespaces = [
    "common",
    "errors",
    "enums",
    "preferences",
    "dashboard",
    "notes",
    "copilot",
    "monitors",
    "calendar",
    "auth",
  ];
  const en: Record<string, unknown> = {};
  for (const ns of namespaces) {
    try {
      // Vite resolves the JSON import at test time; under prod build this
      // path is unreachable because `IS_TEST_ENV` is statically false.
      const mod = (await import(
        /* @vite-ignore */ `../../public/locales/en/${ns}.json`
      )) as { default: unknown };
      en[ns] = mod.default;
    } catch {
      en[ns] = {};
    }
  }
  return { en } as Resource;
}

export const EAGER_NAMESPACES = ["common", "errors", "enums"] as const;
export const LAZY_NAMESPACES = [
  "dashboard",
  "notes",
  "copilot",
  "monitors",
  "calendar",
  "auth",
  "preferences",
] as const;

export const ALL_NAMESPACES = [...EAGER_NAMESPACES, ...LAZY_NAMESPACES] as const;

export const SUPPORTED_FALLBACK = ["en"];

export async function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return i18n;
  let chain = i18n.use(initReactI18next);
  if (!IS_TEST_ENV) {
    chain = chain.use(HttpBackend).use(LanguageDetector);
  }
  // Synchronously bake the EN catalogs into the test bundle so tests
  // render real strings (matching production). TR + AR catalogs are
  // empty in Slice 1 — keys fall back to EN per i18next defaults.
  const testResources = IS_TEST_ENV ? await loadTestResources() : undefined;
  await chain.init({
    fallbackLng: SUPPORTED_FALLBACK,
    // The actual supported list is fetched from GET /api/v1/languages
    // and applied by LanguageProvider; i18next itself stays permissive.
    supportedLngs: false,
    load: "languageOnly", // 'en-US' -> 'en'
    ns: [...EAGER_NAMESPACES],
    defaultNS: "common",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "language",
      caches: ["localStorage"],
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    returnEmptyString: false,
    resources: testResources,
    react: {
      useSuspense: false,
    },
    initImmediate: false,
  });
  return i18n;
}

export default i18n;
